import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import { saveToegangscontrole } from '../Reducers/actions';
import { renderField, renderselect, rendercheckbox } from '../../../../Components/Forms/renders';
import { required, url } from 'redux-form-validators';
import Modal from "../../../../Components/Modal";

const ToegangscontroleForm = ({ 
  onClose, 
  provider,
  handleSubmit, 
  saveToegangscontrole,
  submitting 
}) => {
  const [visible, setVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [authType, setAuthType] = useState('oauth2');

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300); // Wait for animation
  };

  // Update auth type when it changes
  useEffect(() => {
    if (provider === 'doorcy') {
      setAuthType('api_key');
    } else {
      setAuthType('oauth2');
    }
  }, [provider]);

  const onSubmit = async (formValues) => {
    setLoading(true);
    
    const configData = {
      provider: provider || 'salto-ks',
      auth_type: authType,
      client_id: formValues.client_id,
      client_secret: formValues.client_secret,
      callback_url: formValues.callback_url,
      authorization_url: formValues.authorization_url,
      token_url: formValues.token_url,
      api_base_url: formValues.api_base_url,
      scopes: formValues.scopes ? formValues.scopes.split(',').map(s => s.trim()) : null,
      environment: formValues.environment,
      test_mode: formValues.test_mode !== undefined ? formValues.test_mode : true,
      webhook_url: formValues.webhook_url || '',
      beschrijving: formValues.beschrijving || '',
      auto_sync: formValues.auto_sync || false,
      sync_interval: parseInt(formValues.sync_interval) || 3600,
      actief: formValues.actief || false,
      // Additional auth types
      api_key: formValues.api_key,
      bearer_token: formValues.bearer_token,
      username: formValues.username,
      password: formValues.password
    };

    const result = await saveToegangscontrole(configData);
    
    if (result.success) {
      handleClose();
    }
    
    setLoading(false);
  };

  const environmentOptions = provider === 'doorcy' ? {
    production: 'Productie',
    development: 'Development (Test)'
  } : {
    sandbox: 'Sandbox (Test)',
    production: 'Productie'
  };

  return createPortal(
    <Modal visible={visible} onClose={handleClose} width={800}>
      <div className="card">
        <div className="card-header">
          <h4 className="mb-0">
            <i className={`fas ${provider === 'doorcy' ? 'fa-door-open' : 'fa-key'} me-2`}></i>
            {provider === 'doorcy' ? 'Doorcy API Configuratie' : 'Salto KS Configuratie'}
          </h4>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)} className="card-body">
          {loading && (
            <div className="overlay-loading">
              <div className="d-flex flex-column align-items-center">
                <i className="fas fa-spinner fa-spin fa-2x mb-2"></i>
                <p>Configuratie opslaan...</p>
              </div>
            </div>
          )}

          {/* General Settings */}
          <div className="row mb-4">
            <div className="col-12">
              <h5 className="border-bottom pb-2">
                <i className="fas fa-cog me-2"></i>
                Algemene Instellingen
              </h5>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <Field
                name="actief"
                component={rendercheckbox}
                type="checkbox"
                label="Toegangscontrole activeren"
                helpText="Schakel dit in om Salto KS integratie beschikbaar te maken"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <Field
                name="beschrijving"
                component={renderField}
                type="textarea"
                rows="3"
                label="Beschrijving"
                placeholder="Optionele beschrijving voor interne referentie"
              />
            </div>
          </div>

          {/* Authentication Configuration */}
          <div className="row mb-4 mt-4">
            <div className="col-12">
              <h5 className="border-bottom pb-2">
                <i className="fas fa-shield-alt me-2"></i>
                {authType === 'oauth2' ? 'OAuth/OpenID Connect Configuratie' : 
                 authType === 'api_key' ? 'API Key Authenticatie' : 
                 authType === 'bearer_token' ? 'Bearer Token Authenticatie' : 
                 'Basic Authentication'}
              </h5>
            </div>
          </div>

          {authType === 'oauth2' && (
            <>
              <div className="row">
                <div className="col-md-6">
                  <Field
                    name="client_id"
                    component={renderField}
                    type="text"
                    label="Client ID"
                    placeholder={provider === 'doorcy' ? 'Your Doorcy Client ID' : 'Your Salto KS Client ID'}
                    validate={[required({ msg: 'Client ID is verplicht' })]}
                    helpText={`Client ID uit je ${provider === 'doorcy' ? 'Doorcy' : 'Salto KS'} developer portal`}
                  />
                </div>
                <div className="col-md-6">
                  <Field
                    name="client_secret"
                    component={renderField}
                    type="password"
                    label="Client Secret"
                    placeholder={provider === 'doorcy' ? 'Your Doorcy Client Secret' : 'Your Salto KS Client Secret'}
                    validate={[required({ msg: 'Client Secret is verplicht' })]}
                    helpText={`Client Secret uit je ${provider === 'doorcy' ? 'Doorcy' : 'Salto KS'} developer portal`}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <Field
                    name="callback_url"
                    component={renderField}
                    type="url"
                    label="Callback URL"
                    placeholder="https://yourdomain.com/api/admin/toegangscontrole/callback"
                    validate={[required({ msg: 'Callback URL is verplicht' }), url({ msg: 'Voer een geldige URL in' })]}
                    helpText={`URL waar ${provider === 'doorcy' ? 'Doorcy' : 'Salto KS'} je naartoe redirect na autorisatie`}
                  />
                </div>
              </div>
            </>
          )}

          {authType === 'api_key' && (
            <div className="row">
              <div className="col-12">
                <Field
                  name="api_key"
                  component={renderField}
                  type="password"
                  label={provider === 'doorcy' ? 'API Token' : 'API Key'}
                  placeholder={provider === 'doorcy' ? 'Doorcy API Token' : 'Your API Key'}
                  validate={[required({ msg: provider === 'doorcy' ? 'API Token is verplicht' : 'API Key is verplicht' })]}
                  helpText={provider === 'doorcy' ? 'API Token uit je Doorcy admin panel' : `API Key uit je ${provider} dashboard`}
                />
              </div>
            </div>
          )}

          {authType === 'bearer_token' && (
            <div className="row">
              <div className="col-12">
                <Field
                  name="bearer_token"
                  component={renderField}
                  type="password"
                  label="Bearer Token"
                  placeholder="Your Bearer Token"
                  validate={[required({ msg: 'Bearer Token is verplicht' })]}
                  helpText="Bearer token voor API authenticatie"
                />
              </div>
            </div>
          )}

          {authType === 'basic_auth' && (
            <div className="row">
              <div className="col-md-6">
                <Field
                  name="username"
                  component={renderField}
                  type="text"
                  label="Username"
                  placeholder="Username"
                  validate={[required({ msg: 'Username is verplicht' })]}
                  helpText="Username voor Basic Authentication"
                />
              </div>
              <div className="col-md-6">
                <Field
                  name="password"
                  component={renderField}
                  type="password"
                  label="Password"
                  placeholder="Password"
                  validate={[required({ msg: 'Password is verplicht' })]}
                  helpText="Password voor Basic Authentication"
                />
              </div>
            </div>
          )}

          {/* API Endpoints */}
          <div className="row mb-4 mt-4">
            <div className="col-12">
              <h5 className="border-bottom pb-2">
                <i className="fas fa-globe me-2"></i>
                API Endpoints
              </h5>
            </div>
          </div>

          {authType === 'oauth2' && (
            <>
              <div className="row">
                <div className="col-md-6">
                  <Field
                    name="authorization_url"
                    component={renderField}
                    type="url"
                    label="Authorization URL"
                    placeholder="https://identity.my-clay.com/connect/authorize"
                    helpText="OAuth authorization endpoint"
                  />
                </div>
                <div className="col-md-6">
                  <Field
                    name="token_url"
                    component={renderField}
                    type="url"
                    label="Token URL"
                    placeholder="https://clp-accept-identityserver.my-clay.com/connect/token"
                    helpText="OAuth token endpoint"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Field
                    name="api_base_url"
                    component={renderField}
                    type="url"
                    label="API Base URL"
                    placeholder={provider === 'doorcy' ? 'https://api.doorcy.nl' : 'https://api.saltoks.com'}
                    helpText={`Base URL voor ${provider === 'doorcy' ? 'Doorcy' : 'Salto KS'} API calls`}
                  />
                </div>
                <div className="col-md-6">
                  <Field
                    name="scopes"
                    component={renderField}
                    type="text"
                    label="OAuth Scopes"
                    placeholder="openid, profile, salto_api"
                    helpText="Comma-separated list van OAuth scopes"
                  />
                </div>
              </div>
            </>
          )}

          {authType !== 'oauth2' && (
            <div className="row">
              <div className="col-12">
                <Field
                  name="api_base_url"
                  component={renderField}
                  type="url"
                  label="API Base URL"
                  placeholder={provider === 'doorcy' ? 'https://api.doorcy.nl' : 'https://yourdomain.com/api'}
                  validate={[required({ msg: 'API Base URL is verplicht' }), url({ msg: 'Voer een geldige URL in' })]}
                  helpText={`Base URL voor ${provider === 'doorcy' ? 'Doorcy' : provider} API calls`}
                />
              </div>
            </div>
          )}

          {/* Environment Settings */}
          <div className="row mb-4 mt-4">
            <div className="col-12">
              <h5 className="border-bottom pb-2">
                <i className="fas fa-server me-2"></i>
                Environment Instellingen
              </h5>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Field
                name="environment"
                component={renderselect}
                options={environmentOptions}
                label="Environment"
                helpText="Selecteer sandbox voor testen, productie voor live gebruik"
              />
            </div>
            <div className="col-md-6">
              <Field
                name="test_mode"
                component={rendercheckbox}
                type="checkbox"
                label="Test modus"
                helpText="Schakel dit uit voor productie gebruik"
              />
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="row mb-4 mt-4">
            <div className="col-12">
              <h5 className="border-bottom pb-2">
                <i className="fas fa-cogs me-2"></i>
                Geavanceerde Instellingen
              </h5>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Field
                name="webhook_url"
                component={renderField}
                type="url"
                label="Webhook URL"
                placeholder="https://yourdomain.com/webhooks/salto"
                helpText="URL voor Salto KS webhooks (optioneel)"
              />
            </div>
            <div className="col-md-6">
              <Field
                name="sync_interval"
                component={renderField}
                type="number"
                label="Sync Interval (seconden)"
                placeholder="3600"
                helpText="Interval voor automatische synchronisatie"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <Field
                name="auto_sync"
                component={rendercheckbox}
                type="checkbox"
                label="Automatische synchronisatie"
                helpText="Schakel in voor automatische data synchronisatie"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="d-flex justify-content-end gap-2">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={handleClose}
                  disabled={loading}
                >
                  <i className="fas fa-times me-1"></i>
                  Annuleren
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading || submitting}
                >
                  <i className="fas fa-save me-1"></i>
                  {loading ? 'Opslaan...' : 'Configuratie Opslaan'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Modal>,
    document.body
  );
};

// Initialize form with existing values - support multiple providers
const initializeForm = (existingConfig, provider) => {
  // Provider-specific defaults
  const getDefaults = (providerName) => {
    if (providerName === 'doorcy') {
      return {
        authorization_url: '',
        token_url: '',
        api_base_url: 'https://api.doorcy.nl',
        scopes: '',
        environment: 'development',
        auth_type: 'api_key',
        test_mode: true,
        sync_interval: 3600
      };
    }
    // Salto KS defaults
    return {
      authorization_url: 'https://identity.my-clay.com/connect/authorize',
      token_url: 'https://clp-accept-identityserver.my-clay.com/connect/token',
      api_base_url: 'https://api.saltoks.com',
      scopes: 'openid, profile, salto_api',
      environment: 'sandbox',
      auth_type: 'oauth2',
      test_mode: true,
      sync_interval: 3600
    };
  };

  const defaults = getDefaults(provider);

  if (!existingConfig) {
    return defaults;
  }

  const config = existingConfig.configuratie || {};
  const extraSettings = existingConfig.extra_instellingen || {};

  return {
    client_id: config.client_id || '',
    client_secret: config.client_secret || '',
    callback_url: config.callback_url || '',
    authorization_url: config.authorization_url || defaults.authorization_url,
    token_url: config.token_url || defaults.token_url,
    api_base_url: config.api_base_url || defaults.api_base_url,
    scopes: Array.isArray(config.scopes) ? config.scopes.join(', ') : (config.scopes || defaults.scopes),
    environment: config.environment || defaults.environment,
    test_mode: config.test_mode !== undefined ? config.test_mode : defaults.test_mode,
    auth_type: config.auth_type || defaults.auth_type,
    api_key: config.api_key || '',
    bearer_token: config.bearer_token || '',
    username: config.username || '',
    password: config.password || '',
    webhook_url: extraSettings.webhook_url || '',
    beschrijving: extraSettings.beschrijving || '',
    auto_sync: extraSettings.auto_sync || false,
    sync_interval: extraSettings.sync_interval || defaults.sync_interval,
    actief: existingConfig.actief || false
  };
};

const ToegangscontroleFormRedux = reduxForm({
  form: 'toegangscontroleForm',
  enableReinitialize: true
})(ToegangscontroleForm);

const mapStateToProps = (_, ownProps) => ({
  initialValues: initializeForm(ownProps.existingConfig, ownProps.provider)
});

export default connect(mapStateToProps, { saveToegangscontrole })(ToegangscontroleFormRedux);